import { UNKNOWN_BRAND_ID } from '@/modules/brands/constants';
import { type PresApiSearchProduct } from '@/modules/search/aliases';
import { capitalise } from '@/modules/text/helpers';

/**
 * Maps plural / compound product-type slugs to a singular, readable form so alt
 * text reads naturally (e.g. "vest" rather than "vests-tanks-camis").
 * Source of truth: https://github.com/depop/presentation-api/blob/master/src/modules/product/constants.ts
 */
const CATEGORY_PLURALISATION_MAP: Record<string, string> = {
  cardigans: 'cardigan',
  shirts: 'shirt',
  jumpers: 'jumper',
  't-shirts': 't-shirt',
  tshirts: 't-shirt',
  vests: 'vest',
  'vests-tanks-camis': 'vest',
  hoodies: 'hoodie',
  sweatshirts: 'sweatshirt',
  jerseys: 'jersey',
  tracksuits: 'tracksuit',
  jackets: 'jacket',
  suits: 'suit',
  coats: 'coat',
  blazers: 'blazer',
  'capes & ponchos': 'cape, poncho',
  parkas: 'parka',
  windbreakers: 'windbreaker',
  fleeces: 'fleece',
  accessories: 'accessory',
  bags: 'bag',
  belts: 'belt',
  scarves: 'scarf',
  watches: 'watch',
  caps: 'cap',
  hats: 'hat',
  tops: 'top',
  bodysuits: 'bodysuit',
  blouses: 'blouse',
  bralets: 'bralet',
  sweaters: 'sweater',
  corsets: 'corset',
  playsuits: 'playsuit',
  skirts: 'skirt',
  dresses: 'dress',
  jumpsuits: 'jumpsuit',
  rompers: 'romper',
  bras: 'bra',
  robes: 'robe',
  camisoles: 'camisole',
  nightgowns: 'nightgown',
  gilets: 'gilet',
  kimonos: 'kimono',
  wallets: 'wallet',
  'bags & purses': 'bag, purse',
  sets: 'set',
};

function getBrandName(brandName?: string, brandId?: number) {
  if (!brandName || brandId === UNKNOWN_BRAND_ID) {
    return '';
  }
  return brandName;
}

function getGender(gender?: string, isKids?: boolean) {
  if (gender === 'male') {
    return isKids ? `Boys'` : `Men's`;
  }
  if (gender === 'female') {
    return isKids ? `Girls'` : `Women's`;
  }
  if (isKids) {
    return `Kids'`;
  }
  return '';
}

function getColours(colour?: string[]) {
  if (!colour?.length) {
    return '';
  }

  const [colour1, colour2] = colour;
  if (colour1 === 'multi' || colour2 === 'multi') {
    return 'multi colour';
  }

  return colour.join(' and ');
}

function joinWithSpace(values?: string[]) {
  if (!values?.length) {
    return '';
  }
  return values.join(' ');
}

function getProductCategory(productType?: string) {
  if (!productType) {
    return '';
  }

  // Some product types are prefixed with "other-", e.g. "other-bags" or
  // "other-jumpsuit-and-playsuit" — drop the prefix and describe by the rest.
  const productName = productType.startsWith('other-')
    ? productType.slice('other-'.length)
    : productType;
  if (!productName) {
    return '';
  }

  return (
    CATEGORY_PLURALISATION_MAP[productName] ?? productName.replace(/-/g, ' ')
  );
}

type ComposeProductImageDescriptionArgs = {
  brandName?: PresApiSearchProduct['brand_name'];
  brandId?: PresApiSearchProduct['brand_id'];
  attributes?: PresApiSearchProduct['attributes'];
};

/**
 * Composes a descriptive image alt tag from a search product's attributes,
 * e.g. "Nike men's vintage blue streetwear vest".
 */
export function composeProductImageDescription({
  brandName,
  brandId,
  attributes,
}: ComposeProductImageDescriptionArgs): string {
  const terms = [
    getBrandName(brandName ?? attributes?.brand, brandId),
    getGender(attributes?.gender, attributes?.is_kids),
    joinWithSpace(attributes?.source),
    getColours(attributes?.colour),
    joinWithSpace(attributes?.style),
    getProductCategory(attributes?.product_type),
  ];

  const description = terms
    .filter((term) => term)
    .join(' ')
    .toLowerCase();

  return capitalise(description);
}
